import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { CollapsibleSection } from '../../components/CollapsibleSection'
import { WidgetErrorBoundary } from '../insights/widgets/WidgetErrorBoundary'
import {
  approveBulkOperation,
  applyBulkOperation,
  createBulkOperation,
  listBulkEditableFields,
  listBulkOperationItems,
  listBulkOperations,
} from '../../api/bulk'
import { ApiError } from '../../api/problem'
import { fieldLabel, formatFieldValueRecord, humanize, label } from '../../lib/labels'
import {
  BULK_KIND_LABEL,
  BULK_KINDS,
  isBulkApplyAsync,
  type BulkEditableFieldView,
  type BulkKind,
  type BulkOperationItemView,
  type BulkOperationView,
  type BulkScope,
} from '../../types/bulk'

const STATUS_VARIANT: Record<string, StatusPillVariant> = {
  preview: 'neutral',
  awaiting_approval: 'warning',
  applied: 'success',
  partially_applied: 'warning',
  rolled_back: 'muted',
  failed: 'danger',
}

const ITEM_STATUS_VARIANT: Record<string, StatusPillVariant> = {
  pending: 'neutral',
  applied: 'success',
  skipped: 'muted',
  failed: 'danger',
}

/**
 * THE one real target this platform's bulk-operation engine has wired end to end (backend
 * `module.bulk` README: "Exactly ONE real target is wired — `QualifiedSpendLineBulkTarget`").
 * Disclosed, not hidden: the "new bulk edit" form below only ever targets this entity because no
 * other `BulkTargetPort` exists yet to target — the field ALLOW-LIST itself still comes from the
 * real `GET /bulk-editable-fields` read, never a hardcoded field list.
 */
const REAL_TARGET_ENTITY = 'qualified_spend_line'

/** `bulk_operation.status`/`bulk_operation_item.status` — free-text lifecycle values (V23 CHECK);
 * {@link humanize} is a sufficient business label here (no curated dictionary needed beyond
 * Title Case), same fallback the central label library exposes for any other unanticipated key. */
function statusLabel(status: string): string {
  return humanize(status)
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

/**
 * `/data/bulk` — the Bulk Operations screen (this story). Surfaces `module.bulk`'s guarded-write
 * boundary end to end: `GET /bulk-operations` (thin additive GET, this story) lists past and
 * in-progress operations for the tenant; each row expands to its `GET /bulk-operations/{id}/items`
 * per-row outcome (already-published). "New bulk edit" walks the REAL
 * create -> preview -> approve -> apply state machine (`BulkOperationController`, already-published
 * except the field-picker GET, also added this story) — a field flagged `requiresReason` (e.g.
 * `qualifying_pct`) can ONLY be applied with a non-blank reason, which the server independently
 * re-validates regardless of what this form sends: the mandatory comment threads onto every
 * constituent row's `AdjustmentService` call via `BulkAdjustmentRouter`, the ONE sanctioned
 * guarded-write path (CLAUDE.md §2.1) — this screen never writes a guarded field any other way.
 */
export function BulkOperationsScreen() {
  const [refreshKey, setRefreshKey] = useState(0)
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null)

  const handleCreated = useCallback((op: BulkOperationView) => {
    setJustCreatedId(op.id)
    setRefreshKey((k) => k + 1)
  }, [])

  return (
    <div
      style={{
        padding: 'var(--iw-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--iw-space-4)',
        height: '100%',
        overflow: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)' }}>
        <span style={{ color: 'var(--iw-accent)', display: 'inline-flex' }}>
          <Icon name="rows" size={22} />
        </span>
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>Bulk operations</h1>
          <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
            Preview, approve, and apply bulk edits across a filtered or hand-picked row set
            (FR-M20.6/M20.7). Every guarded field (e.g. qualifying %) routes through the adjustment
            ledger with a mandatory reason — never a direct write.
          </p>
        </div>
      </div>

      <WidgetErrorBoundary title="New bulk edit" icon="edit-3">
        <CollapsibleSection title="New bulk edit" defaultOpen={false}>
          <NewBulkEditPanel onCreated={handleCreated} />
        </CollapsibleSection>
      </WidgetErrorBoundary>

      <WidgetErrorBoundary title="Bulk operations" icon="rows">
        <BulkOperationsListPanel refreshKey={refreshKey} justCreatedId={justCreatedId} />
      </WidgetErrorBoundary>
    </div>
  )
}

function NewBulkEditPanel({ onCreated }: { onCreated: (op: BulkOperationView) => void }) {
  const [fields, setFields] = useState<BulkEditableFieldView[] | null>(null)
  const [fieldsError, setFieldsError] = useState<string | null>(null)
  const [scope, setScope] = useState<BulkScope>('selection')
  const [selectionText, setSelectionText] = useState('')
  const [filterExpr, setFilterExpr] = useState('')
  const [selectedField, setSelectedField] = useState('')
  const [fieldValue, setFieldValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [created, setCreated] = useState<BulkOperationView | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    listBulkEditableFields(REAL_TARGET_ENTITY, controller.signal)
      .then((rows) => {
        setFields(rows)
        if (rows.length > 0) setSelectedField((prev) => prev || rows[0]?.field || '')
      })
      .catch((err) => setFieldsError(errorMessage(err, 'Could not load the bulk-editable field list.')))
    return () => controller.abort()
  }, [])

  const activeField = useMemo(() => fields?.find((f) => f.field === selectedField) ?? null, [fields, selectedField])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    setCreated(null)
    if (!activeField) {
      setFormError('Choose a field to change.')
      return
    }
    if (fieldValue.trim() === '') {
      setFormError('Enter the new value.')
      return
    }
    const kind: BulkKind = 'update'
    let selection: string[] | null = null
    if (scope === 'selection') {
      selection = selectionText
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
      if (selection.length === 0) {
        setFormError('Paste at least one row id (comma- or newline-separated).')
        return
      }
    } else if (filterExpr.trim() === '') {
      setFormError('Enter a filter expression (e.g. category==in_state_spend).')
      return
    }

    setSubmitting(true)
    try {
      const op = await createBulkOperation({
        kind,
        targetEntity: REAL_TARGET_ENTITY,
        scope,
        selection: scope === 'selection' ? selection : null,
        filterExpr: scope === 'filter' ? filterExpr.trim() : null,
        fieldChanges: { [activeField.field]: fieldValue.trim() },
      })
      setCreated(op)
      onCreated(op)
      setSelectionText('')
      setFilterExpr('')
      setFieldValue('')
    } catch (err) {
      setFormError(errorMessage(err, 'Could not create this bulk operation.'))
    } finally {
      setSubmitting(false)
    }
  }

  if (fieldsError) {
    return <InlineAlert severity="error">{fieldsError}</InlineAlert>
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
        Target entity: <strong>{label('entity', REAL_TARGET_ENTITY)}</strong> — the one entity the
        bulk-edit engine is wired to end to end today (film qualified-spend lines). Kind: bulk
        update.
      </p>

      <div role="group" aria-label="Scope" style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
        <button type="button" className="iw-chip" aria-pressed={scope === 'selection'} onClick={() => setScope('selection')}>
          By row id
        </button>
        <button type="button" className="iw-chip" aria-pressed={scope === 'filter'} onClick={() => setScope('filter')}>
          By filter
        </button>
      </div>

      {scope === 'selection' ? (
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="bulk-selection">
            Row ids (comma- or newline-separated)
          </label>
          <textarea
            id="bulk-selection"
            className="iw-input"
            rows={2}
            value={selectionText}
            onChange={(e) => setSelectionText(e.target.value)}
            placeholder="e.g. 3f2a1c9e-... , 91bd44f0-..."
          />
        </div>
      ) : (
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="bulk-filter">
            Filter expression (RSQL)
          </label>
          <input
            id="bulk-filter"
            className="iw-input"
            value={filterExpr}
            onChange={(e) => setFilterExpr(e.target.value)}
            placeholder="category==in_state_spend"
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="bulk-field">
            Field to change
          </label>
          <select
            id="bulk-field"
            className="iw-select"
            value={selectedField}
            onChange={(e) => setSelectedField(e.target.value)}
            disabled={!fields}
          >
            {(fields ?? []).map((f) => (
              <option key={f.field} value={f.field}>
                {fieldLabel(f.field)}
                {f.guarded ? ' (guarded)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="bulk-value">
            New value
          </label>
          <input
            id="bulk-value"
            className="iw-input"
            value={fieldValue}
            onChange={(e) => setFieldValue(e.target.value)}
          />
        </div>
        <button type="submit" className="iw-btn iw-btn--primary" disabled={submitting || !fields}>
          <Icon name="plus" size={14} /> Preview bulk edit
        </button>
      </div>

      {activeField?.guarded ? (
        <InlineAlert severity="warning">
          <strong>{fieldLabel(activeField.field)}</strong> is a guarded field — applying this
          operation will require a reason and routes through the adjustment ledger, never a direct
          write.
        </InlineAlert>
      ) : null}

      {formError ? <InlineAlert severity="error">{formError}</InlineAlert> : null}

      {created ? (
        <InlineAlert severity="info">
          Created bulk operation <code>{created.id}</code> — {created.totalCount} row(s) resolved.
          Find it below to approve and apply.
        </InlineAlert>
      ) : null}
    </form>
  )
}

function BulkOperationsListPanel({ refreshKey, justCreatedId }: { refreshKey: number; justCreatedId: string | null }) {
  const [statusFilter, setStatusFilter] = useState('')
  const [kindFilter, setKindFilter] = useState('')
  const [rows, setRows] = useState<BulkOperationView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const fetchRows = useCallback(() => {
    setError(null)
    setRows(null)
    listBulkOperations({ status: statusFilter || null, kind: kindFilter || null, limit: 50 })
      .then((page) => setRows(page.items))
      .catch((err) => setError(errorMessage(err, 'Could not load bulk operations.')))
  }, [statusFilter, kindFilter])

  useEffect(() => {
    fetchRows()
  }, [fetchRows, refreshKey])

  useEffect(() => {
    if (justCreatedId) setExpandedId(justCreatedId)
  }, [justCreatedId])

  function handleRowChanged(updated: BulkOperationView) {
    setRows((prev) => prev?.map((r) => (r.id === updated.id ? updated : r)) ?? prev)
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div role="group" aria-label="Status filter" style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
          <button type="button" className="iw-chip" aria-pressed={statusFilter === ''} onClick={() => setStatusFilter('')}>
            All statuses
          </button>
          {['preview', 'awaiting_approval', 'applied', 'partially_applied', 'failed'].map((s) => (
            <button key={s} type="button" className="iw-chip" aria-pressed={statusFilter === s} onClick={() => setStatusFilter(s)}>
              {statusLabel(s)}
            </button>
          ))}
        </div>
        <div role="group" aria-label="Kind filter" style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
          <button type="button" className="iw-chip" aria-pressed={kindFilter === ''} onClick={() => setKindFilter('')}>
            Update + delete
          </button>
          {BULK_KINDS.map((k) => (
            <button key={k} type="button" className="iw-chip" aria-pressed={kindFilter === k} onClick={() => setKindFilter(k)}>
              {BULK_KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={fetchRows}>
              Try again
            </button>
          }
        >
          {error}
        </InlineAlert>
      ) : rows === null ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading bulk operations…</p>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-6)' }}>
          <Icon name="rows" size={28} />
          <p>No bulk operations yet for this tenant. Use "New bulk edit" above to create one.</p>
        </div>
      ) : (
        <table className="iw-table" aria-label="Bulk operations">
          <thead>
            <tr>
              <th scope="col">Kind</th>
              <th scope="col">Target entity</th>
              <th scope="col">Scope</th>
              <th scope="col">Status</th>
              <th scope="col">Counts</th>
              <th scope="col">Created</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {rows.map((op) => (
              <OperationRow
                key={op.id}
                op={op}
                expanded={expandedId === op.id}
                onToggle={() => setExpandedId((cur) => (cur === op.id ? null : op.id))}
                onChanged={handleRowChanged}
              />
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

interface OperationRowProps {
  op: BulkOperationView
  expanded: boolean
  onToggle: () => void
  onChanged: (op: BulkOperationView) => void
}

function OperationRow({ op, expanded, onToggle, onChanged }: OperationRowProps) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer' }} aria-expanded={expanded}>
        <td>{BULK_KIND_LABEL[op.kind as BulkKind] ?? op.kind}</td>
        <td>{label('entity', op.targetEntity)}</td>
        <td>{label('bulkScope', op.scope)}</td>
        <td>
          <StatusPill variant={STATUS_VARIANT[op.status] ?? 'neutral'} label={statusLabel(op.status)} />
        </td>
        <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
          {op.appliedCount} applied · {op.failedCount} failed · {op.skippedCount} skipped (of {op.totalCount})
        </td>
        <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>{formatDateTime(op.createdAt)}</td>
        <td>
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={14} />
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={7} style={{ background: 'var(--iw-accent-subtle)' }}>
            <OperationDetail op={op} onChanged={onChanged} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

function OperationDetail({ op, onChanged }: { op: BulkOperationView; onChanged: (op: BulkOperationView) => void }) {
  const [items, setItems] = useState<BulkOperationItemView[] | null>(null)
  const [itemsError, setItemsError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionPending, setActionPending] = useState(false)
  const [reason, setReason] = useState('')
  const [asyncJobId, setAsyncJobId] = useState<string | null>(null)

  const loadItems = useCallback(() => {
    setItemsError(null)
    listBulkOperationItems(op.id)
      .then(setItems)
      .catch((err) => setItemsError(errorMessage(err, 'Could not load this operation’s row detail.')))
  }, [op.id])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  async function handleApprove() {
    setActionError(null)
    setActionPending(true)
    try {
      const updated = await approveBulkOperation(op.id)
      onChanged(updated)
    } catch (err) {
      setActionError(errorMessage(err, 'Could not approve this bulk operation.'))
    } finally {
      setActionPending(false)
    }
  }

  async function handleApply() {
    setActionError(null)
    setAsyncJobId(null)
    setActionPending(true)
    try {
      const outcome = await applyBulkOperation(op.id, reason.trim() || undefined)
      if (isBulkApplyAsync(outcome)) {
        setAsyncJobId(outcome.jobId)
      } else {
        onChanged(outcome)
        loadItems()
      }
    } catch (err) {
      setActionError(errorMessage(err, 'Could not apply this bulk operation. If it touches a '
        + 'guarded field, a reason is required.'))
    } finally {
      setActionPending(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)', padding: 'var(--iw-space-3) 0' }}>
      {op.status === 'preview' ? (
        <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'center' }}>
          <button type="button" className="iw-btn iw-btn--primary iw-btn--sm" onClick={() => void handleApprove()} disabled={actionPending}>
            <Icon name="check" size={12} /> Approve
          </button>
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
            Approval is required before this operation can be applied (AC6).
          </span>
        </div>
      ) : null}

      {op.status === 'awaiting_approval' ? (
        <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="iw-field">
            <label className="iw-field-label" htmlFor={`reason-${op.id}`}>
              Reason (required if any touched field is guarded)
            </label>
            <input
              id={`reason-${op.id}`}
              className="iw-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Corrected per client-provided vendor invoice"
            />
          </div>
          <button type="button" className="iw-btn iw-btn--primary iw-btn--sm" onClick={() => void handleApply()} disabled={actionPending}>
            <Icon name="check-circle" size={12} /> Apply
          </button>
        </div>
      ) : null}

      {asyncJobId ? (
        <InlineAlert severity="info">
          This batch exceeded the synchronous threshold — it was submitted as background job{' '}
          <code>{asyncJobId}</code>. Track it from Data &amp; bulk ops → Recent activity.
        </InlineAlert>
      ) : null}

      {actionError ? <InlineAlert severity="error">{actionError}</InlineAlert> : null}

      {itemsError ? (
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={loadItems}>
              Try again
            </button>
          }
        >
          {itemsError}
        </InlineAlert>
      ) : items === null ? (
        <p style={{ color: 'var(--iw-text-muted)', margin: 0 }}>Loading rows…</p>
      ) : items.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', margin: 0 }}>No rows resolved for this operation.</p>
      ) : (
        <table className="iw-table" aria-label="Bulk operation rows">
          <thead>
            <tr>
              <th scope="col">Row</th>
              <th scope="col">Status</th>
              <th scope="col">Before</th>
              <th scope="col">After</th>
              <th scope="col">Error</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td style={{ fontSize: 'var(--iw-size-xs)' }}>
                  <EntityRef kind="record" id={item.entityId} name={label('entity', item.entityType)} />
                </td>
                <td>
                  <StatusPill variant={ITEM_STATUS_VARIANT[item.status] ?? 'neutral'} label={statusLabel(item.status)} />
                </td>
                <td style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                  {formatFieldValueRecord(item.before)}
                </td>
                <td style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                  {item.after ? formatFieldValueRecord(item.after) : '— (populated on apply)'}
                </td>
                <td style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-danger)' }}>{item.error ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
